import { useCallback, useState } from 'react';
import {
  newImageDraftKey,
  putDraftImages,
  takeDraftImages,
  type DraftImageGroups,
} from './imageDraftStore';

/**
 * Keep a form's uploaded images alive across a Back-and-return.
 *
 * Six forms independently decided images were unsaveable — five of them carry the
 * identical comment `// Images can't be serialized, so we won't include them` — and so
 * each restored an image field it never emitted. After a Back the grid came back empty
 * and, because every one of those forms gates `isFormValid` on `images.length > 0`,
 * Continue was dead until each file was re-picked from disk (#378, #463, #468).
 *
 * The comment was right about the storage and wrong about the conclusion: a File is not
 * JSON and a blob: preview dies with its document, so the images stay in memory and only
 * a JSON-safe token travels in the draft. After a reload the store is empty, the token
 * misses, and the form honestly starts with none.
 *
 * Written once as a hook rather than pasted into six files, because pasting is how this
 * defect reached six files.
 *
 * Callers must ALSO:
 *  - put `imageDraftKey` in their `onFormDataChange` payload (restore ⊆ emit), and
 *  - not revoke blob URLs on unmount. Those previews are exactly what the store is
 *    holding; revoking them on the way out restores dead thumbnails, which is worse
 *    than restoring nothing. Revoke only when the user removes an image.
 */
export function useImageDraft(initialFormData: Record<string, unknown>) {
  // A lazy useState rather than a ref: the key is created once and never changes, and
  // reading `useRef(...).current` during render trips react-hooks/refs. Same class of fix
  // as increment 01's image seeding — change the shape, don't suppress the rule.
  const [imageDraftKey] = useState<string>(
    () => (initialFormData.imageDraftKey as string) || newImageDraftKey(),
  );

  // Read as a state SEED, not in an effect: the images have to be on the first painted
  // frame, or the form flashes empty and briefly disables its own Continue button.
  const [restored] = useState<DraftImageGroups | null>(() =>
    takeDraftImages(initialFormData.imageDraftKey as string | undefined),
  );

  /** Replaces, never merges — a group the caller omits is a group the user emptied. */
  const persistImages = useCallback(
    (groups: DraftImageGroups) => putDraftImages(imageDraftKey, groups),
    [imageDraftKey],
  );

  return { imageDraftKey, restored, persistImages };
}
